/**
 * Needs exist to shape the player's day, not to be babysat.
 *
 * Two needs only: hunger and energy. They decay slowly enough that the player
 * eats about once a day and sleeps about once a night, which is exactly the
 * rhythm that makes the day boundary meaningful. A low need slows work rather
 * than blocking it, so the player is nudged, never stalled.
 */
import type { GameState, NeedKey } from '../state/GameState.js';
import type { EventBus } from '../core/events.js';
import { BALANCE } from '../data/balance.js';
import { getHousing, FOOD_BY_ID } from '../data/catalog.js';
import type { FinanceSystem } from './FinanceSystem.js';

export class NeedsSystem {
  private warned: Record<NeedKey, boolean> = { hunger: false, energy: false };

  constructor(private readonly bus: EventBus) {}

  tick(state: GameState, minutes: number, working: boolean): void {
    const hours = minutes / 60;
    const n = state.player.needs;

    if (state.time.sleeping) {
      const rest = getHousing(state.player.housingId).restQuality;
      n.energy.value = clamp(
        n.energy.value + BALANCE.needs.energyRecoveryPerHour * rest * hours,
      );
      n.hunger.value = clamp(n.hunger.value - BALANCE.needs.hungerDecayPerHour * 0.5 * hours);
    } else {
      const energyRate = working
        ? BALANCE.needs.energyDecayPerHourWorking
        : BALANCE.needs.energyDecayPerHour;
      n.energy.value = clamp(n.energy.value - energyRate * hours);
      n.hunger.value = clamp(n.hunger.value - BALANCE.needs.hungerDecayPerHour * hours);
    }

    for (const key of ['hunger', 'energy'] as NeedKey[]) {
      const low = n[key].value < BALANCE.needs.lowThreshold;
      if (low && !this.warned[key]) {
        this.warned[key] = true;
        this.bus.emit({ type: 'need.critical', need: key });
      } else if (!low) {
        this.warned[key] = false;
      }
    }
  }

  /**
   * Multiplier applied to work speed and, through the quality curve, to how
   * good the work is. Both needs low compounds, so neglecting yourself is
   * genuinely expensive - but it is never a hard stop, because a player with no
   * money must always be able to work their way out.
   *
   * Calibrated against the cost of food: an early balance sweep found that
   * simply never eating beat playing properly, because the penalty was worth
   * less than the grocery bill. Running on empty now costs roughly two thirds
   * of throughput, which is far more than a day's food.
   */
  performanceMultiplier(state: GameState): number {
    let m = 1;
    for (const key of ['hunger', 'energy'] as NeedKey[]) {
      const value = state.player.needs[key].value;
      if (value < BALANCE.needs.criticalThreshold) m *= BALANCE.needs.criticalPenalty;
      else if (value < BALANCE.needs.lowThreshold) m *= BALANCE.needs.lowPenalty;
    }
    return m;
  }

  eat(state: GameState, foodId: string, finance: FinanceSystem): boolean {
    const food = FOOD_BY_ID.get(foodId);
    if (!food) return false;
    if (!finance.debit(state, food.price, 'food', food.name)) return false;
    state.player.needs.hunger.value = clamp(state.player.needs.hunger.value + food.hunger);
    state.player.needs.energy.value = clamp(state.player.needs.energy.value + food.energy);
    return true;
  }
}

function clamp(v: number): number {
  return Math.max(0, Math.min(100, v));
}
